/**
 * Available Apps Selectors
 * Memoized selectors for the availableApps Redux slice.
 *
 * Task 33 — Requirements: 5.4, 5.5
 */

import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../index';
import { SysAppDto } from '../../services/api/sysAppService';

// ============================================================================
// Base Selectors
// ============================================================================

/**
 * Selects the full availableApps slice state.
 */
export const selectAvailableAppsState = (state: RootState) => state.availableApps;

/**
 * Selects the raw apps array from the slice (may include module entries if the
 * backend behaves unexpectedly — use selectVisibleApps for client-safe results).
 */
export const selectAvailableApps = (state: RootState): SysAppDto[] => state.availableApps.apps;

/**
 * Selects the loading flag.
 */
export const selectAvailableAppsIsLoading = (state: RootState): boolean => state.availableApps.isLoading;

/**
 * Selects the error string, or null when there is no error.
 */
export const selectAvailableAppsError = (state: RootState): string | null => state.availableApps.error;

// ============================================================================
// Computed Selectors (Memoized)
// ============================================================================

/**
 * Returns the list of apps and tools that should be rendered as launcher
 * tiles, excluding any entry where classification === 'module'.
 *
 * Per edi-module-registration the BE GetAvailableAsync now INCLUDES module
 * rows (Party Manager always; EDI when subscribed) — this selector strips
 * them so they don't show up as launcher tiles. Module presence is consumed
 * via selectActiveTenantSubscribesToEdiModule and similar predicates.
 */
export const selectVisibleApps = createSelector(
  [selectAvailableApps],
  (apps): SysAppDto[] => apps.filter((app) => app.classification !== 'module')
);

/**
 * Returns true iff the active tenant subscribes to the EDI module.
 *
 * IMPORTANT: this selector reads from `state.workspace.apps`, NOT
 * `state.availableApps.apps`. The `availableApps` slice is populated by
 * `fetchAvailableApps()` which calls the BE with NO tenantId — so the BE
 * only ever returns public-access apps in that slice (EDI is `private`
 * and will never appear). The `workspace.apps` slice is populated by
 * `fetchWorkspaceApps(tenantId)` and DOES include the tenant's
 * subscribed private apps + the EDI module row when subscribed.
 *
 * On the `/main` (non-tenant) page, `workspace.apps` is empty, so the
 * selector returns false — which is correct (no active tenant ⇒ no EDI
 * surfaces).
 *
 * Consumers: Header.tsx (mounts ActivePartySelector), AppShell.tsx
 * (renders the edi-admin side-panel group). When false, both surfaces are
 * omitted entirely.
 *
 * Spec: openspec/changes/edi-module-registration/
 */
export const selectActiveTenantSubscribesToEdiModule = createSelector(
  [(state: RootState) => state.workspace.apps],
  (apps): boolean =>
    apps.some((a) => a.classification === 'module' && a.appCode === 'edi' && a.isActive)
);
